import React, { useState, useEffect } from 'react';
import { User, ChevronDown, Check, Users } from 'lucide-react';
import { useStatus } from '../../contexts/StatusContext';
import { familyService } from '../../api/familyService';
import { familyPINService } from '../../api/familyPINService';
import { FamilyMember as KidFamilyMember } from '../../types/familyMember';
import toast from 'react-hot-toast';

interface FamilyMember {
  user_id: string;
  display_name: string;
  email: string;
  user_type: string;
  avatar?: string;
}

interface CombinedMember {
  id: string;
  name: string;
  type: 'cognito' | 'family_member';
  sub_type?: string; // user_type or 'child'
  avatar?: string;
  age?: number;
}

interface AssignmentPickerProps {
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
  disabled?: boolean;
  allowMultiple?: boolean;
  selectedUserIds?: string[];
  onSelectMultiple?: (userIds: string[]) => void;
}

const AssignmentPicker: React.FC<AssignmentPickerProps> = ({
  selectedUserId,
  onSelect,
  disabled = false,
  allowMultiple = false,
  selectedUserIds = [],
  onSelectMultiple
}) => {
  const { family_id, user_id } = useStatus();
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [kidMembers, setKidMembers] = useState<KidFamilyMember[]>([]);
  const [combinedMembers, setCombinedMembers] = useState<CombinedMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Load family members (both Cognito users and family PIN kids)
  useEffect(() => {
    if (family_id) {
      loadAllMembers();
    }
  }, [family_id]);

  const loadAllMembers = async () => {
    if (!family_id) return;

    setLoading(true);
    try {
      // Load Cognito family members
      const cognitoMembers = await familyService.getMembers(family_id);
      setFamilyMembers(cognitoMembers);

      // Load family PIN kids (ages 5-12)
      try {
        const pinMembersResponse = await familyPINService.getFamilyMembers();
        setKidMembers(pinMembersResponse.members);
      } catch (error) {
        // Family PIN may not be set up yet, that's okay
        console.log('[AssignmentPicker] Family PIN members not available:', error);
        setKidMembers([]);
      }
    } catch (error) {
      console.error('[AssignmentPicker] Failed to load family members:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  // Combine both types of members
  useEffect(() => {
    const combined: CombinedMember[] = [
      // Cognito users
      ...familyMembers.map(member => ({
        id: member.user_id,
        name: member.display_name,
        type: 'cognito' as const,
        sub_type: member.user_type,
        avatar: member.avatar
      })),
      // Family PIN kids
      ...kidMembers.map(kid => ({
        id: kid.member_id,
        name: kid.member_name,
        type: 'family_member' as const,
        sub_type: 'child',
        avatar: kid.avatar_emoji,
        age: kid.member_age
      }))
    ];
    setCombinedMembers(combined);
  }, [familyMembers, kidMembers]);

  const handleSelect = (userId: string) => {
    if (allowMultiple && onSelectMultiple) {
      // Handle multiple selection
      const newSelection = selectedUserIds.includes(userId)
        ? selectedUserIds.filter(id => id !== userId)
        : [...selectedUserIds, userId];
      onSelectMultiple(newSelection);
    } else {
      // Handle single selection
      onSelect(userId === selectedUserId ? null : userId);
      setIsOpen(false);
    }
  };

  const getSelectedMember = () => {
    if (allowMultiple) {
      return combinedMembers.filter(m => selectedUserIds.includes(m.id));
    }
    return combinedMembers.find(m => m.id === selectedUserId);
  };

  const selectedMember = getSelectedMember();
  const displayName = allowMultiple
    ? (selectedMember as CombinedMember[]).length > 0
      ? `${(selectedMember as CombinedMember[]).length} selected`
      : 'Select members...'
    : (selectedMember as CombinedMember | undefined)?.name || 'Select a person...';

  return (
    <div className="relative">
      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:border-honey-500 focus:ring-2 focus:ring-honey-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-gray-400" />
          <span className={selectedUserId || selectedUserIds.length > 0 ? '' : 'text-gray-400'}>
            {loading ? 'Loading...' : displayName}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && !loading && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute z-20 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {/* Clear selection option */}
            {!allowMultiple && selectedUserId && (
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-400" />
                </div>
                <span className="text-gray-500 dark:text-gray-400 italic">
                  Unassign
                </span>
              </button>
            )}

            {/* Family members */}
            {combinedMembers.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                No family members found
              </div>
            ) : (
              <>
                {combinedMembers.map(member => {
                  const isSelected = allowMultiple
                    ? selectedUserIds.includes(member.id)
                    : selectedUserId === member.id;
                  const isCurrentUser = member.type === 'cognito' && member.id === user_id;
                  const isFamilyMember = member.type === 'family_member';

                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => handleSelect(member.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
                        isSelected ? 'bg-honey-50 dark:bg-honey-900/20' : ''
                      }`}
                    >
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                        isFamilyMember
                          ? 'text-2xl'
                          : 'bg-gradient-to-br from-honey-400 to-orange-500 text-white'
                      }`}>
                        {member.avatar || member.name.charAt(0).toUpperCase()}
                      </div>

                      {/* Name and info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          {member.name}
                          {isCurrentUser && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              (You)
                            </span>
                          )}
                          {isFamilyMember && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                              <Users className="w-3 h-3" />
                              Kid
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {isFamilyMember
                            ? `Age ${member.age} · Family member`
                            : member.sub_type
                          }
                        </div>
                      </div>

                      {/* Selected indicator */}
                      {isSelected && (
                        <Check className="w-5 h-5 text-honey-600 dark:text-honey-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssignmentPicker;
